import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Columns2, Columns3, X } from 'lucide-react'
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { FilterButton } from '@/components/Filters'
import { FormError } from '@/components/FormError'
import { page } from '@/components/layout'
import { SelectToggle } from '@/components/Selection'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { albumPhotoUrl, useStream, type Albums } from '@/lib/albums'
import type { Family, Space } from '@/lib/families'
import { useFrame } from '@/lib/frame'
import {
  ringKey,
  ringList,
  SIZE_KEY,
  SIZES,
  showOf,
  showValue,
  tabOf,
  useRings,
  wallQuery,
  type PhotoTab,
  type Ring,
  type Show,
  type WallPhoto,
  type WallSize,
} from '@/lib/modern'
import { morph } from '@/lib/motion'
import { useIsPhone } from '@/lib/platform'
import { remember, remembered } from '@/lib/remember'
import { useStuck } from '@/lib/stuck'
import type { Selection } from '@/lib/selection'
import { cn } from '@/lib/utils'
import { PhotoPost } from './PhotoPost'
import { RingRow, Story } from './Stories'
import { SlidingTabs, TabsEnd } from './SwipeTabs'
import { useTabSwipe } from './useTabSwipe'
import { useOpenPhoto } from './useOpenPhoto'
import { Wall, type WallHandle } from './Wall'
import { Loader } from '@/components/Loader'
import { BarRow } from '@/components/nav/BarRow'

type Tab = { key: PhotoTab; label: string }
type Person = Albums['people'][number]

// Photos in its Modern style (shared/sections.ts): rings along the top for On this day and the people with new
// photos, three tabs (All, Albums, People) that a sideways swipe moves between, and the wall of whole photos. A
// person picked on People, or a year or word picked under Filter, narrows the wall, and says so beside the tabs. A photo opens over the wall as a post and goes back into its place when it closes; a ring plays
// as a story. Where you are is kept in the address (?show=, ?photo=, ?ring=), so Back closes what's open and a link
// opens the same view.
export function ModernPhotos({
  family,
  space,
  albums,
  mine,
  onMine,
  selection,
  moments,
  shelves,
  sendPicked,
}: {
  family: Family
  space: Space | null
  albums: Albums
  mine: boolean
  onMine: (mine: boolean) => void
  selection: Selection
  moments: boolean
  // The albums, laid out as the Traditional style shelves them, for the Albums tag.
  shelves: ReactNode
  // The bar for sending picked photos to an album, given the photos on the wall.
  sendPicked: (photos: WallPhoto[]) => ReactNode
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const [params, setParams] = useSearchParams()
  const legacy = params.get('person')
    ? `person:${params.get('person')}`
    : params.get('year')
      ? `year:${params.get('year')}`
      : params.get('tag')
        ? `tag:${params.get('tag')}`
        : null
  const asked = showOf(params.get('show') ?? legacy)
  // The router changes the address in a transition, which a view transition can't wait for, so what the wall shows
  // changes here at once and the address catches up behind it. Kept only until the morph is done, and only while the
  // address still says what it said before.
  const [landing, setLanding] = useState<{ show: Show; from: string } | null>(null)
  const show = landing && showValue(asked) === landing.from ? landing.show : asked
  const rings = useRings(family.id, space?.id)
  const stream = useStream(family.id, 'added', mine, space?.id)
  const tagged = useQuery({
    ...wallQuery(family.id, show, space?.id, mine),
    enabled: show.kind !== 'all' && show.kind !== 'albums' && show.kind !== 'people',
  })
  const photos: WallPhoto[] = useMemo(
    () => (show.kind === 'all' ? (stream.data?.pages.flatMap((p) => p.photos) ?? []) : (tagged.data ?? [])),
    [show.kind, stream.data, tagged.data],
  )
  const listed = show.kind === 'albums' || show.kind === 'people'
  const loaded = show.kind === 'all' ? !!stream.data : listed || !!tagged.data
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = stream
  const more = show.kind === 'all' && hasNextPage
  const loadMore = useMemo(
    () => () => {
      if (!isFetchingNextPage) void fetchNextPage()
    },
    [isFetchingNextPage, fetchNextPage],
  )
  const wall = useRef<WallHandle>(null)
  // Two photos across a phone, or three; on bigger screens one more column than usual. The photos slide to their new
  // places.
  const [size, setSize] = useState<WallSize>(() => remembered(SIZE_KEY, SIZES, 'bigger'))
  const resize = (next: WallSize) => {
    remember(SIZE_KEY, next)
    morph('wall', () => setSize(next))
  }

  // ---- Narrowing the wall: the pill moves at once, then the wall rearranges itself once the photos are here.
  const [pending, setPending] = useState<string | null>(null)
  const latest = useRef<string | null>(null)
  const change = (next: Show, also?: (q: URLSearchParams) => void) => {
    const key = showValue(next)
    const q = new URLSearchParams(params)
    for (const old of ['show', 'person', 'year', 'tag']) q.delete(old)
    if (key !== 'all') q.set('show', key)
    also?.(q)
    selection.stop()
    setParams(q, { replace: true, state: location.state })
  }
  const choose = async (next: Show) => {
    const key = showValue(next)
    if (key === showValue(show)) return
    latest.current = key
    setPending(key)
    if (next.kind !== 'all' && next.kind !== 'albums' && next.kind !== 'people')
      await queryClient.ensureQueryData(wallQuery(family.id, next, space?.id, mine)).catch(() => null)
    if (latest.current !== key) return
    morph(
      'wall',
      () => {
        setLanding({ show: next, from: showValue(asked) })
        change(next)
        setPending(null)
      },
      { done: () => setLanding(null) },
    )
  }

  // ---- A photo opened: it flies from its tile into the post, and back into its tile when the post closes.
  const { openId, withPhoto, open, close, go, swap, putAway } = useOpenPhoto(wall)
  // A tag tapped on a post: the post goes and the wall comes back narrowed to it, from the top.
  const showFromPost = (next: Show) =>
    morph(
      'close',
      () => {
        putAway()
        setLanding({ show: next, from: showValue(asked) })
        change(next, (q) => q.delete('photo'))
      },
      { landed: () => scrollTo({ top: 0, behavior: 'instant' }), done: () => setLanding(null) },
    )

  // ---- Stories
  const ringParam = params.get('ring')
  const allRings = useMemo(() => (rings.data ? ringList(rings.data) : []), [rings.data])
  const ring = allRings.find((r) => ringKey(r) === ringParam) ?? null
  const [from, setFrom] = useState<DOMRect | null>(null)
  const openRing = (r: Ring, rect: DOMRect) => {
    setFrom(rect)
    const q = withPhoto(null)
    q.set('ring', ringKey(r))
    setParams(q, { state: { opened: true } })
  }
  const toRing = (r: Ring) => {
    const q = new URLSearchParams(params)
    q.set('ring', ringKey(r))
    setParams(q, { replace: true, state: location.state })
  }
  const closeRing = () => {
    if ((location.state as { opened?: boolean } | null)?.opened) navigate(-1)
    else {
      const q = new URLSearchParams(params)
      q.delete('ring')
      setParams(q, { replace: true })
    }
  }
  const photoFromStory = go

  // ---- The tabs, and what narrows the wall
  const tabs = useMemo(() => tabsFor(albums), [albums])
  const active = pending ? tabOf(showOf(pending)) : tabOf(show)
  const narrowed = narrowedBy(albums, show)
  const known = openId ? photos.find((p) => p.id === openId) : undefined
  const phone = useIsPhone()
  const { actionSlot } = useFrame()
  const years = [...albums.years].sort((p, q) => q.year.localeCompare(p.year))
  const words = albums.tags.slice(0, 12)
  const filter = (
    <FilterButton
      title="Filter"
      className={phone ? 'order-first bg-transparent hover:bg-transparent active:opacity-60' : undefined}
      filters={[
        {
          heading: 'Whose',
          label: 'Whose',
          value: mine ? 'mine' : 'everyone',
          usual: 'everyone',
          options: [
            ['everyone', 'Everyone’s'],
            ['mine', 'Only mine'],
          ],
          onChange: (w) => onMine(w === 'mine'),
        },
        {
          heading: 'Grid',
          label: 'Grid',
          value: size,
          options: phone
            ? [
                ['bigger', '2 wide'],
                ['smaller', '3 wide'],
              ]
            : [
                ['bigger', 'Bigger'],
                ['smaller', 'Smaller'],
              ],
          icons: { bigger: Columns2, smaller: Columns3 },
          onChange: (v) => resize(v as WallSize),
        },
        ...(years.length
          ? [
              {
                heading: 'Year',
                label: 'Year',
                value: show.kind === 'year' ? show.year : 'any',
                usual: 'any',
                options: [['any', 'Any'] as [string, string], ...years.map((y): [string, string] => [y.year, y.year])],
                onChange: (y: string) => void choose(y === 'any' ? { kind: 'all' } : { kind: 'year', year: y }),
              },
            ]
          : []),
        ...(words.length
          ? [
              {
                heading: 'Tagged',
                label: 'Tagged',
                value: show.kind === 'tag' ? show.tag : 'any',
                usual: 'any',
                options: [
                  ['any', 'Any'] as [string, string],
                  ...words.map((t): [string, string] => [t.tag, `#${t.tag}`]),
                ],
                onChange: (t: string) => void choose(t === 'any' ? { kind: 'all' } : { kind: 'tag', tag: t }),
              },
            ]
          : []),
      ]}
    />
  )

  // ---- Swiping between the tabs (SwipeTabs.tsx).
  const { root, tabRow, pager } = useTabSwipe({
    keys: tabs.map((t) => t.key),
    active: tabOf(show),
    still: !selection.selecting && !openId && !ring,
    onTurn: (key) => {
      selection.stop()
      setLanding({ show: { kind: key }, from: showValue(asked) })
      change({ kind: key })
      setPending(null)
    },
    onLanded: () => setLanding(null),
  })
  useStuck(tabRow)

  return (
    <div ref={root} className="flex flex-col gap-4 nav:gap-5">
      {rings.data && <RingRow rings={rings.data} onOpen={openRing} />}
      <BarRow ref={tabRow} pinned className={cn(page, 'mp-tabs flex items-center gap-2 max-sm:px-0')}>
        <SlidingTabs tabs={tabs} active={active} onChoose={(key) => void choose({ kind: key })} />
        {narrowed && (
          <button
            type="button"
            className="mp-narrowed"
            data-face={narrowed.face ? '' : undefined}
            aria-label={`Showing ${narrowed.label}. Show ${narrowed.back.kind === 'all' ? 'all photos' : narrowed.back.kind}`}
            onClick={() => void choose(narrowed.back)}
          >
            {narrowed.face && <Avatar person={narrowed.face} className="size-6 text-[0.625rem] shadow-none" />}
            <span className="truncate">{narrowed.label}</span>
            <X className="size-4 shrink-0" aria-hidden />
          </button>
        )}
        <TabsEnd>
          {phone ? actionSlot && createPortal(filter, actionSlot) : filter}
          {!listed && photos.length > 0 && <SelectToggle selection={selection} />}
        </TabsEnd>
      </BarRow>
      <div ref={pager} className={cn(page, 'flex flex-col gap-6 max-sm:px-3')}>
        <FormError error={show.kind === 'all' ? stream.error : tagged.error} />
        {show.kind === 'albums' ? (
          <div className="-mx-5 max-sm:-mx-2 sm:-mx-8 lg:-mx-12">{shelves}</div>
        ) : show.kind === 'people' ? (
          <PeopleCards people={albums.people} onChoose={(id) => void choose({ kind: 'person', id })} />
        ) : loaded && photos.length === 0 ? (
          <p className="px-2 text-muted-foreground">
            {mine
              ? 'You haven’t added any photos here.'
              : show.kind === 'all'
                ? 'No photos yet. Add the first from the “+”.'
                : 'No photos here yet.'}
          </p>
        ) : (
          // A thin line from the screen's edges on a phone, like the space between photos.
          <div className="max-sm:-mx-2">
            <Wall
              ref={wall}
              photos={photos}
              label={narrowed?.label ?? 'All'}
              selected={selection.selected}
              onToggle={selection.selecting ? selection.toggle : undefined}
              onOpen={open}
              more={more}
              onMore={loadMore}
              smaller={size === 'smaller'}
            />
          </div>
        )}
        {more && (
          <div className="flex justify-center">
            {isFetchingNextPage ? (
              <Loader label="Loading more photos" now />
            ) : (
              <Button variant="outline" onClick={loadMore}>
                Show more photos
              </Button>
            )}
          </div>
        )}
        {sendPicked(photos)}
      </div>
      {openId && (
        <PhotoPost
          id={openId}
          known={known}
          list={photos}
          familyId={family.id}
          moments={moments}
          onClose={close}
          onGo={go}
          onSwap={swap}
          onShow={showFromPost}
        />
      )}
      {ring && (
        <Story
          familyId={family.id}
          spaceId={space?.id}
          ring={ring}
          rings={allRings}
          from={from}
          onRing={toRing}
          onClose={closeRing}
          onOpenPhoto={photoFromStory}
        />
      )}
    </div>
  )
}

// The tabs: every photo, the albums (when there are any), and the people in the photos (when someone's tagged).
function tabsFor(albums: Albums): Tab[] {
  return [
    { key: 'all', label: 'All' },
    ...(albums.albums.length ? [{ key: 'albums', label: 'Albums' } as Tab] : []),
    ...(albums.people.length ? [{ key: 'people', label: 'People' } as Tab] : []),
  ]
}

// What the wall is narrowed to, if anything: its name, and where its × goes (a person back to People, an album back to
// Albums, a year or a word back to every photo).
function narrowedBy(albums: Albums, show: Show): { label: string; back: Show; face?: Person } | null {
  if (show.kind === 'person') {
    const face = albums.people.find((p) => p.id === show.id)
    return { label: face?.name ?? 'Someone', back: { kind: 'people' }, face }
  }
  if (show.kind === 'album')
    return { label: albums.albums.find((a) => a.id === show.id)?.title ?? 'An album', back: { kind: 'albums' } }
  if (show.kind === 'year') return { label: show.year, back: { kind: 'all' } }
  if (show.kind === 'tag') return { label: `#${show.tag}`, back: { kind: 'all' } }
  return null
}

// The People tab: a card for each person in the photos, with a few of their newest photos, most photographed first.
// Picking one shows their photos, as if they'd been picked under a filter.
function PeopleCards({ people, onChoose }: { people: Person[]; onChoose: (id: string) => void }) {
  return (
    <ul className="grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-3 lg:grid-cols-4" aria-label="People">
      {people.map((p) => {
        const covers = p.covers ?? []
        return (
          <li key={p.id}>
            <button
              type="button"
              className="mp-person flex w-full flex-col gap-2 text-left"
              onClick={() => onChoose(p.id)}
            >
              <span className="mp-collage" data-count={Math.min(covers.length, 4)} aria-hidden>
                {covers.slice(0, 4).map((ref) => (
                  <img key={ref} src={albumPhotoUrl(ref, 'thumb')} alt="" loading="lazy" decoding="async" />
                ))}
                {covers.length === 0 && <Avatar person={p} className="size-16 text-xl shadow-none" />}
              </span>
              <span className="flex min-w-0 items-center gap-2 px-0.5">
                <Avatar person={p} className="size-7 text-xs shadow-none" />
                <span className="min-w-0">
                  <span className="block truncate font-semibold">{p.name}</span>
                  <span className="block text-sm text-muted-foreground">
                    {p.photos === 1 ? '1 photo' : `${p.photos} photos`}
                  </span>
                </span>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
